import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from "sonner";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiClock, FiSave } from 'react-icons/fi';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  getAllTasksForStudent, getCoachNote, saveCoachNote, getWeekStartDate, SUBJECT_COLORS, getExamColor
} from '../../utils/coachUtils';
import '../../styles/CoachPanel.css';

export default function CoachStudentDetailPage() {
  const { id: studentId } = useParams();
  const navigate = useNavigate();
  const coachId = localStorage.getItem('userId');

  const [student, setStudent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const coachName = localStorage.getItem('username') || 'Koç';

  // Modal States
  const [showExamsModal, setShowExamsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [examsData, setExamsData] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [isFetchingExams, setIsFetchingExams] = useState(false);
  const [isFetchingStats, setIsFetchingStats] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const studentRef = doc(db, 'users', studentId);
      const studentDoc = await getDoc(studentRef);
      if (studentDoc.exists()) setStudent({ id: studentDoc.id, ...studentDoc.data() });

      const allTasks = await getAllTasksForStudent(studentId);
      setTasks(allTasks);

      const coachNote = await getCoachNote(coachId, studentId);
      if (coachNote) setNote(coachNote.content);
    } catch (err) {
      console.error('Error fetching student detail:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, coachId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveNote = async () => {
    setNoteSaving(true);
    try {
      await saveCoachNote(coachId, studentId, note);
      toast.success('Not kaydedildi ✓');
      setIsEditingNote(false);
    } catch { toast.error('Not kaydedilemedi'); }
    finally { setNoteSaving(false); }
  };

  const fetchExams = async () => {
    setIsFetchingExams(true);
    try {
      const q = query(collection(db, 'denemeler'), where('userId', '==', studentId));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Firestore sort by timestamp is easier client side if not indexed
      data.sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return dateB - dateA;
      });
      setExamsData(data);
      setShowExamsModal(true);
    } catch (err) {
      toast.error('Sınav sonuçları yüklenemedi: ' + err.message);
    } finally {
      setIsFetchingExams(false);
    }
  };

  const fetchStats = async () => {
    setIsFetchingStats(true);
    try {
      const q = query(collection(db, 'studyEntries'), where('userId', '==', studentId));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return dateB - dateA;
      });
      setStatsData(data);
      setShowStatsModal(true);
    } catch (err) {
      toast.error('İstatistikler yüklenemedi: ' + err.message);
    } finally {
      setIsFetchingStats(false);
    }
  };

  // Stats
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const skippedTasks = tasks.filter(t => t.status === 'skipped').length;
  const pendingTasks = totalTasks - completedTasks - skippedTasks;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const thisWeekStart = getWeekStartDate();
  const thisWeekTasks = tasks.filter(t => t.weekStartDate === thisWeekStart);
  const thisWeekCompleted = thisWeekTasks.filter(t => t.status === 'completed').length;
  const thisWeekRate = thisWeekTasks.length > 0 ? Math.round((thisWeekCompleted / thisWeekTasks.length) * 100) : 0;

  // Subject breakdown
  const subjectStats = {};
  tasks.forEach(t => {
    if (!subjectStats[t.subject]) subjectStats[t.subject] = { total: 0, completed: 0 };
    subjectStats[t.subject].total++;
    if (t.status === 'completed') subjectStats[t.subject].completed++;
  });

  if (loading) {
    return (<div className="coach-page"><div className="coach-loading"><div className="coach-loading-spinner" /><span>Yükleniyor...</span></div></div>);
  }

  if (!student) {
    return (<div className="coach-page"><div className="coach-empty-state"><h3>Öğrenci bulunamadı</h3></div></div>);
  }

  return (
    <div className="coach-page">
      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/coach')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 24, padding: '8px 16px', borderRadius: 100,
          fontSize: 12, fontWeight: 600, background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)', color: 'var(--text-muted)',
          cursor: 'pointer',
        }}
        whileHover={{ background: 'rgba(255,255,255,0.06)' }}
        whileTap={{ scale: 0.97 }}
      >
        <FiArrowLeft size={14} /> Panele Dön
      </motion.button>

      {/* Student Header (Centered) */}
      <motion.div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: getExamColor(student.examType), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 800, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <span style={{ fontSize: 40 }}>🦉</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', marginTop: 12 }}>{student.username}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{student.school || 'Okul Belirtilmemiş'}</div>
        
        <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#4f46e5' }}>
            <span style={{ fontSize: 16 }}>🎓</span> {student.examType || 'MF'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#4f46e5' }}>
            <span style={{ fontSize: 16 }}>#</span> {student.grade || 'Mezun'}
          </div>
        </div>
      </motion.div>

      {/* Button Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Tanışma Formu', action: () => toast.info('Yakında eklenecek!') },
          { label: 'Ara', action: () => toast.info('Yakında eklenecek!') },
          { label: 'Tabloları', action: () => toast.info('Yakında eklenecek!') },
          { label: 'Sınav Sonuçları', action: fetchExams, loading: isFetchingExams },
          { label: 'Baykuş Denemeleri', action: () => toast.info('Yakında eklenecek!') },
          { label: 'Kronometre İstatistikleri', action: fetchStats, loading: isFetchingStats }
        ].map(btn => (
          <button 
            key={btn.label}
            onClick={btn.action}
            disabled={btn.loading}
            style={{ padding: '12px', borderRadius: '12px', background: '#ffffff', border: '1px solid #e2e8f0', color: '#1e293b', fontSize: '13px', fontWeight: 700, cursor: btn.loading ? 'wait' : 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', opacity: btn.loading ? 0.7 : 1 }}
          >
            {btn.loading ? 'Yükleniyor...' : btn.label}
          </button>
        ))}
      </div>

      {/* Coach Info */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 30 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>👩‍🏫</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
          {coachName} <span style={{ color: '#64748b', fontWeight: 500 }}>(Takım Lideri)</span>
        </div>
      </div>

      {/* Coach Notes */}
      <motion.div className="coach-notes-section" style={{ marginTop: 10 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
        <div className="coach-notes-title" style={{ justifyContent: 'space-between' }}>
          <span>📝 Koç Notları</span>
          {!isEditingNote && (
            <button 
              onClick={() => setIsEditingNote(true)}
              style={{ fontSize: 12, color: '#4f46e5', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 700 }}
            >
              Düzenle
            </button>
          )}
        </div>
        
        {isEditingNote ? (
          <>
            <textarea
              className="coach-notes-textarea"
              placeholder="Bu öğrenci hakkında değerlendirme notlarınız, güçlü/zayıf yönleri, tavsiyeler..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="coach-notes-save" onClick={handleSaveNote} disabled={noteSaving}>
                <FiSave size={12} style={{ marginRight: 6 }} />
                {noteSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button className="coach-notes-save" style={{ background: '#f1f5f9', color: '#475569' }} onClick={() => { setIsEditingNote(false); setNote(note); }}>
                İptal
              </button>
            </div>
          </>
        ) : (
          <div style={{ padding: 16, fontSize: 14, color: '#4f46e5', lineHeight: 1.6, fontWeight: 500, whiteSpace: 'pre-wrap', textAlign: 'center' }}>
            {note || 'Öğrenci hakkında henüz bir not eklenmemiş.'}
          </div>
        )}
      </motion.div>

      {/* Sınav Sonuçları Modal */}
      <AnimatePresence>
        {showExamsModal && (
          <motion.div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowExamsModal(false)}>
            <motion.div style={{ background: '#fff', width: '100%', maxWidth: 600, borderRadius: 20, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Sınav Sonuçları ({examsData.length})</h3>
                <button onClick={() => setShowExamsModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {examsData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Henüz deneme kaydı yok.</div>
                ) : (
                  examsData.map(exam => (
                    <div key={exam.id} style={{ background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, color: '#4f46e5' }}>{exam.type === 'genel' ? 'Genel Deneme' : 'Branş Denemesi'}</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                          {exam.yorumlar?.tarih || (exam.timestamp?.toDate ? exam.timestamp.toDate().toLocaleDateString('tr-TR') : 'Tarih Yok')}
                        </span>
                      </div>
                      <div style={{ fontSize: 14, color: '#1e293b', marginBottom: 12 }}><strong>Yayın:</strong> {exam.yorumlar?.yayin || 'Belirtilmemiş'}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#10b981', marginBottom: 12 }}>Toplam Net: {exam.toplamNet || 0}</div>
                      
                      {exam.yorumlar && (
                        <div style={{ fontSize: 13, color: '#475569', background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                          <div style={{ marginBottom: 4 }}><strong>Zorlanılan:</strong> {exam.yorumlar.zor || '-'}</div>
                          <div style={{ marginBottom: 4 }}><strong>Hata Nedeni:</strong> {exam.yorumlar.neden || '-'}</div>
                          <div><strong>Telafi:</strong> {exam.yorumlar.telafi || '-'}</div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kronometre İstatistikleri Modal */}
      <AnimatePresence>
        {showStatsModal && (
          <motion.div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowStatsModal(false)}>
            <motion.div style={{ background: '#fff', width: '100%', maxWidth: 600, borderRadius: 20, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1e293b' }}>Çalışma Saati Kayıtları ({statsData.length})</h3>
                <button onClick={() => setShowStatsModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>
              
              <div style={{ padding: 20, background: '#f1f5f9', display: 'flex', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Toplam Çalışma Süresi</div>
                  <div style={{ fontSize: 32, fontWeight: 900, color: '#4f46e5' }}>
                    {Math.floor(statsData.reduce((acc, curr) => acc + (curr.duration || 0), 0) / 3600)} <span style={{ fontSize: 16 }}>sa</span>{' '}
                    {Math.floor((statsData.reduce((acc, curr) => acc + (curr.duration || 0), 0) % 3600) / 60)} <span style={{ fontSize: 16 }}>dk</span>
                  </div>
                </div>
              </div>

              <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {statsData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Henüz çalışma kaydı yok.</div>
                ) : (
                  statsData.map(stat => {
                    const hours = Math.floor((stat.duration || 0) / 3600);
                    const minutes = Math.floor(((stat.duration || 0) % 3600) / 60);
                    return (
                      <div key={stat.id} style={{ background: '#fff', padding: '12px 16px', borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiClock size={18} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: '#1e293b' }}>
                              {hours > 0 && `${hours} sa `}{minutes > 0 && `${minutes} dk`}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {stat.date || (stat.timestamp?.toDate ? stat.timestamp.toDate().toLocaleDateString('tr-TR') : '-')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
